<script lang="ts" setup>
import LabelTag from "~/components/form/LabelTag.vue";
import InputTag from "~/components/form/InputTag.vue";
import SelectTag from "~/components/form/SelectTag.vue";
import { STATES } from "@/constants/states";
import CheckboxTag from "~/components/form/CheckboxTag.vue";
import RadioTag from "~/components/form/RadioTag.vue";
import H3Heading from "~/components/H3Heading.vue";
import creditCardType from "credit-card-type";
import InputAddressTag from "~/components/form/InputAddressTag.vue";

const config = useRuntimeConfig();
const router = useRouter();

// Properties
const props = defineProps({
    showCheckboxSave: {
        type: Boolean,
        default: false,
    },
    showCheckboxDefaultPayment: {
        type: Boolean,
        default: false,
    },
    initialData: {
        type: Object,
        default: {},
    },
    shippingAddress: {
        type: Object,
        default: {},
    },
    isEdit: {
        type: Boolean,
        default: false,
    },
    inCheckout: {
        type: Boolean,
        default: false,
    },
});

// Data
const store = useCheckoutStore();
const currentYear = new Date().getFullYear() - 1;

const form = ref({
    id: "",
    paymentType: "cc",
    paymentProfileId: 0,
    cardType: "",
    cardNumber: "",
    cardholderName: "",
    expMonth: "",
    expYear: "",
    cvv: "",
    saveCard: false, // Member only + New card
    setAsDefault: false, // Member only
    useShippingAddress: false, // On checkout, allow guest/member to use shipping address. Hide this on add/edit.

    street: "",
    city: "",
    state: "",
    zip: "",
});

// Functions
function setFormData() {
    if (props.initialData && props.initialData.payment && props.initialData.payment.cardNumber) {
        const data = props.initialData.payment;
        form.value.id = props.initialData.id;

        form.value.paymentProfileId = data.paymentProfileId ?? 0;
        form.value.cardType = data.cardType;
        form.value.cardNumber = props.initialData.id ? "" : data.cardNumber; // Force user to re-enter
        form.value.cardholderName = data.cardholderName;
        form.value.expMonth = data.expMonth;
        form.value.expYear = data.expYear;
        form.value.cvv = data.cvv;
        form.value.saveCard = data.saveCard;
        form.value.setAsDefault = !!data.setAsDefault;

        form.value.useShippingAddress = data.useShippingAddress;
        form.value.street = data.street;
        form.value.city = data.city;
        form.value.state = data.state;
        form.value.zip = data.zip;
    }
}

function validateAndSave() {
    let status = false;
    const data = form.value;

    if (data && data.cardNumber && data.cardholderName && data.expMonth && data.expYear && data.cvv) {
        if (data.useShippingAddress || (data.street && data.city && data.state && data.zip)) {
            status = true;
            data.cardType = creditCardType(data.cardNumber)[0]?.type ?? "visa"; // Set cc type
            if (props.inCheckout) {
                store.updatePayment(data);
            }
        }
    }

    // This is being used in checkout only
    store.updatePlaceOrderEnabled(props.inCheckout ? status : false);
}

onMounted(() => {
    setFormData();
});
onUpdated(() => {
    validateAndSave();
});

defineExpose({ form });
</script>

<template>
    <div>
        <div v-if="isEdit">
            <H3Heading size="text-lg">Edit Credit Card</H3Heading>
            <div class="mb-4 flex md:mb-8">
                <div class="mr-3 w-8 flex-none">
                    <img
                        v-show="form.cardType?.toLowerCase().includes('american')"
                        src="/images/checkout/AXP.svg"
                        class="w-full"
                    />
                    <img
                        v-show="form.cardType?.toLowerCase().includes('master')"
                        src="/images/checkout/mc_symbol.svg"
                        class="w-full"
                    />
                    <img
                        v-show="form.cardType?.toLowerCase().includes('visa')"
                        src="/images/checkout/Visa.svg"
                        class="mt-2 w-full"
                    />
                    <img
                        v-show="form.cardType?.toLowerCase().includes('discover')"
                        src="/images/checkout/DGN.png"
                        class="w-full"
                    />
                </div>
                <div class="flex-1 font-bold">
                    <span v-if="form.cardType?.toLowerCase().includes('american')"
                        >American Express ending in {{ initialData.payment.cardNumber }}</span
                    >
                    <span v-else-if="form.cardType?.toLowerCase().includes('master')"
                        >Mastercard ending in {{ initialData.payment.cardNumber }}</span
                    >
                    <span v-else-if="form.cardType?.toLowerCase().includes('visa')"
                        >Visa ending in {{ initialData.payment.cardNumber }}</span
                    >
                    <span v-else-if="form.cardType?.toLowerCase().includes('discover')"
                        >Discovery ending in {{ initialData.payment.cardNumber }}</span
                    >
                </div>
            </div>
        </div>

        <div>
            <LabelTag for="cardNumber">Card Number *</LabelTag>
            <InputTag id="cardNumber" v-model="form.cardNumber" class="w-full rounded-sm" required />
        </div>
        <div class="mt-4 md:flex">
            <div class="flex-1 md:pr-4">
                <LabelTag for="cardholderName">Cardholder Name *</LabelTag>
                <InputTag id="cardholderName" v-model="form.cardholderName" class="w-full rounded-sm" required />
            </div>
            <div class="mt-4 flex-none md:mt-0 md:w-80 md:pr-4">
                <div class="md:flex">
                    <div class="flex-none md:w-60 md:pr-4">
                        <LabelTag for="cardExpMonth">Expiration Date *</LabelTag>
                        <div class="flex">
                            <SelectTag
                                id="cardExpMonth"
                                v-model="form.expMonth"
                                class="mr-4 w-full flex-1 rounded-sm md:w-36"
                                required
                            >
                                <option v-for="n in 12" :key="`cc-month-${n}`" :value="n">{{ n }}</option>
                            </SelectTag>
                            <SelectTag
                                id="cardExpYear"
                                v-model="form.expYear"
                                class="w-full flex-1 rounded-sm"
                                required
                            >
                                <option v-for="n in 20" :key="`cc-year-${n}`" :value="n + currentYear">
                                    {{ n + currentYear }}
                                </option>
                            </SelectTag>
                        </div>
                    </div>
                    <div class="mt-4 w-full flex-none md:mt-0 md:w-20">
                        <LabelTag for="cardCVV">CVV *</LabelTag>
                        <InputTag id="cardCVV" v-model="form.cvv" class="w-full rounded-sm" maxlength="3" required />
                    </div>
                </div>
            </div>
        </div>

        <H3Heading size="text-xl" class="mt-4">Billing Address</H3Heading>
        <!-- Give user the option to use shipping address  -->
        <div v-if="inCheckout && shippingAddress.street">
            <ul class="mb-4">
                <li>
                    <RadioTag v-model="form.useShippingAddress" :value="true">Use Shipping Address</RadioTag>
                    <div class="indent-7 text-sm font-black">
                        {{ shippingAddress.street }}, {{ shippingAddress.city }}, {{ shippingAddress.state }}
                        {{ shippingAddress.zip }}
                    </div>
                </li>
                <li class="mt-4">
                    <RadioTag v-model="form.useShippingAddress" :value="false">Enter a Different Address</RadioTag>
                </li>
            </ul>
        </div>

        <!-- Address -->
        <div v-if="!form.useShippingAddress">
            <div>
                <LabelTag for="street">Address *</LabelTag>
                <InputAddressTag
                    v-model:street="form.street"
                    v-model:city="form.city"
                    v-model:state="form.state"
                    v-model:zip="form.zip"
                    class="w-full rounded-sm"
                    placeholder="Enter your address"
                    required
                />
            </div>
            <div class="mt-4 md:flex">
                <div class="flex-1 md:pr-4">
                    <LabelTag for="city">City *</LabelTag>
                    <InputTag id="city" v-model="form.city" autocomplete="on" class="w-full rounded-sm" required />
                </div>
                <div class="mt-4 flex-none md:mt-0 md:w-52 md:pr-4">
                    <LabelTag for="city">State *</LabelTag>
                    <SelectTag id="state" v-model="form.state" autocomplete="on" class="w-full rounded-sm" required>
                        <option v-for="(val, key) in STATES" :value="key">{{ val }}</option>
                    </SelectTag>
                </div>
                <div class="mt-4 flex-none md:mt-0 md:w-32">
                    <LabelTag for="zip">ZIP Code *</LabelTag>
                    <InputTag id="zip" v-model="form.zip" autocomplete="on" class="w-full rounded-sm" required />
                </div>
            </div>
        </div>

        <div v-if="showCheckboxSave || showCheckboxDefaultPayment" class="mt-4 text-sm">
            <CheckboxTag v-if="showCheckboxSave" v-model="form.saveCard">
                Save this card for future purchases
            </CheckboxTag>
            <CheckboxTag v-if="showCheckboxDefaultPayment" v-model="form.setAsDefault" class="mt-2">
                Set as my default payment method
            </CheckboxTag>
        </div>
    </div>
</template>
